import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getPlatformLogo } from '../types';

interface VideoMetric {
  video: string;
  conversions: number;
  revenue: number;
  conversionRate: number;
}

interface PlatformDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platformId: string;
  platformName: string;
  videos: VideoMetric[];
}

export function PlatformDetailsModal({
  open,
  onOpenChange,
  platformId,
  platformName,
  videos
}: PlatformDetailsModalProps) {
  useLanguage();
  const avgTicket = videos.length > 0 
    ? videos.reduce((sum, v) => sum + v.revenue, 0) / videos.reduce((sum, v) => sum + v.conversions, 0) 
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <img 
              src={getPlatformLogo(platformId)} 
              alt={platformName}
              className={`object-contain ${platformId === 'braip' ? 'max-h-[28px] max-w-[70px]' : 'max-h-[32px] max-w-[90px]'}`}
            />
            <span className="text-[18px] font-semibold text-[#111827]">{translateInterfaceText("Detalhes da")}{" "}{platformName}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-6">
          <div className="bg-white border border-[#E7E9EF] rounded-[14px] shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAFA] border-b border-[#E7E9EF] hover:bg-[#FAFAFA]">
                  <TableHead className="font-semibold text-sm text-[#374151]">{translateInterfaceText("Vídeo")}</TableHead>
                  <TableHead className="text-right font-semibold text-sm text-[#374151]">{translateInterfaceText("Conversões")}</TableHead>
                  <TableHead className="text-right font-semibold text-sm text-[#374151]">{translateInterfaceText("Ticket Médio")}</TableHead>
                  <TableHead className="text-right font-semibold text-sm text-[#374151]">{translateInterfaceText("Receita")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {videos.map((video, index) => {
                  const videoTicket = video.conversions > 0 ? video.revenue / video.conversions : 0;
                  return (
                    <TableRow key={index} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]">
                      <TableCell className="text-[15px] text-[#111827]">{video.video}</TableCell>
                      <TableCell className="text-right text-[15px] text-[#111827]">{video.conversions}</TableCell>
                      <TableCell className="text-right text-[15px] text-[#111827]">
                        R$ {videoTicket.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right text-[15px] text-[#111827]">
                        R$ {video.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
